import Phaser from 'phaser';
import { WORLD_TILES, TILE_SIZE } from '@shared/constants';
import type { MapData } from '@shared/types/map';
import { getSurfaceById, getSurfaceTextureKey } from '../../sprites/SurfaceSpriteManager';
import { minimapColorFor, MINIMAP_COLOR_BG, MINIMAP_COLOR_BLOCKED } from './minimapColors';

/**
 * Bake del terreno del mapa a UNA textura con los **sprites REALES** (suelo, caminos,
 * árboles, agua, entradas), a resolución `pxPerTile`. Es la misma textura que usan tanto
 * el world-map overlay del juego (`MapOverlay`) como el minimapa in-game (`Minimap`):
 * se cachea por id de mapa (`map_overlay_<id>`), así abrir/cerrar/zoomear no recrea nada y
 * no se duplica VRAM. Fuente única de verdad del horneado real.
 */

/**
 * px de textura por tile, auto-elegido (sin config) según las capacidades del equipo.
 * El bake es de lado `WORLD_TILES * pxPerTile` px → VRAM ≈ lado² × 4 bytes:
 *   8 px/tile = 8192² ≈ 256MB | 6 = 6144² ≈ 144MB | 4 = 4096² ≈ 64MB.
 *
 * Se combinan dos límites:
 *  1. Límite duro de la GPU (`MAX_TEXTURE_SIZE`, cap a 8192).
 *  2. Heurística de memoria vía `navigator.deviceMemory` (GB de RAM; Chromium) — el mejor
 *     proxy disponible desde el browser. En equipos con poca RAM baja la resolución (y la
 *     VRAM, que es residente porque el minimapa está siempre activo) a cambio de algo de
 *     nitidez al zoom máximo. En equipos capaces usa el cap completo de la GPU.
 */
export function resolvePxPerTile(scene: Phaser.Scene): number {
  let maxTex = 4096;
  try {
    const renderer = scene.sys.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
    const gl = renderer?.gl;
    if (gl) {
      maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    }
  } catch {
    maxTex = 4096;
  }
  let cap = Math.min(8192, maxTex);

  const nav = navigator as Navigator & { deviceMemory?: number };
  const mem = nav.deviceMemory;
  if (typeof mem === 'number' && mem > 0) {
    if (mem <= 4) cap = Math.min(cap, 4096); // ~64MB
    else if (mem <= 8) cap = Math.min(cap, 6144); // ~144MB
    // > 8 GB → cap completo de la GPU (hasta 8192, ~256MB).
  }

  return Math.max(1, Math.floor(cap / WORLD_TILES));
}

export interface BakedMapTexture {
  key: string;
  pxPerTile: number;
  texSize: number;
}

/**
 * Hornea (o reusa si ya existe) la textura del mapa con sprites reales. Idempotente por id.
 */
export function bakeMapTexture(scene: Phaser.Scene, map: MapData): BakedMapTexture {
  const key = `map_overlay_${map.id}`;
  const pxPerTile = resolvePxPerTile(scene);
  const texSize = WORLD_TILES * pxPerTile;

  if (scene.textures.exists(key)) {
    return { key, pxPerTile, texSize };
  }

  const P = pxPerTile;
  const S = texSize;
  console.log(
    `[minimapBake] horneando ${key} @ ${P}px/tile (${S}x${S}, ~${Math.round((S * S * 4) / 1048576)}MB VRAM)`
  );
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('[minimapBake] no se pudo obtener canvas context');
    return { key, pxPerTile, texSize };
  }

  // Fondo negro (mismo color que el mundo del juego).
  ctx.fillStyle = hexToCss(MINIMAP_COLOR_BG);
  ctx.fillRect(0, 0, S, S);

  // Hornear cada tile con su sprite REAL escalado a P px/tile. Capas en orden (suelo → deco).
  for (const layer of map.layers) {
    if (typeof layer.name === 'string' && layer.name.startsWith('_')) continue;
    // MISMO anclaje que el juego (MapTileRenderer): suelo centrado en el tile; decos/techos
    // anclados al borde inferior del tile y creciendo hacia el norte. Con el anclaje viejo
    // (esquina sup-izq) los sprites altos (techos) quedaban corridos hacia abajo/derecha.
    const isGroundLike =
      layer.name === 'ground' ||
      layer.name === 'ground_deco_under' ||
      layer.name === 'layer1_ground' ||
      layer.name === 'layer2_floor_objects';
    for (const tile of layer.tiles) {
      if (tile.x < 0 || tile.x >= WORLD_TILES || tile.y < 0 || tile.y >= WORLD_TILES) continue;
      if (!tile.sprite) {
        if (tile.blocked) {
          ctx.fillStyle = hexToCss(MINIMAP_COLOR_BLOCKED);
          ctx.fillRect(tile.x * P, tile.y * P, P, P);
        }
        continue;
      }

      // Resolver textura (igual que el juego: key directa o surface key).
      let textureKey = tile.sprite;
      const surfaceKey = getSurfaceTextureKey(tile.sprite);
      if (scene.textures.exists(surfaceKey)) {
        textureKey = surfaceKey;
      }

      if (scene.textures.exists(textureKey)) {
        const tex = scene.textures.get(textureKey);
        const src = tex.getSourceImage() as CanvasImageSource;
        const srcW = (tex.source[0]?.width ?? TILE_SIZE) as number;
        const srcH = (tex.source[0]?.height ?? TILE_SIZE) as number;
        // Tamaño de dibujo = tamaño NATIVO del sprite escalado a P px/tile (un bloque 4x4 =
        // 4 tiles; un techo de 256px = 8 tiles de ancho aunque ancle en 1 tile).
        const dw = Math.max(1, Math.round((srcW / TILE_SIZE) * P));
        const dh = Math.max(1, Math.round((srcH / TILE_SIZE) * P));
        // Ancla (igual que el juego): X centrada en el bloque del surface (tilesX, 1 si no es
        // surface — el arte del techo desborda su tile); Y = centro del bloque si es suelo, o
        // borde inferior del tile (crece hacia arriba) si es deco/techo.
        const surfaceDef = getSurfaceById(tile.sprite);
        const anchorTilesX = surfaceDef?.tilesX ?? 1;
        const anchorTilesY = surfaceDef?.tilesY ?? 1;
        const drawX = Math.round(tile.x * P + (anchorTilesX * P) / 2 - dw / 2);
        const drawY = isGroundLike
          ? Math.round(tile.y * P + (anchorTilesY * P) / 2 - dh / 2)
          : Math.round(tile.y * P + P - dh);
        ctx.drawImage(src, drawX, drawY, dw, dh);
      } else {
        // Fallback: color de familia (no dejar huecos si falta la textura).
        const color = minimapColorFor(tile.sprite, tile.blocked === true);
        if (color !== null) {
          ctx.fillStyle = hexToCss(color);
          const span = tile.sprite.endsWith('4x4') ? 4 * P : P;
          ctx.fillRect(tile.x * P, tile.y * P, span, span);
        }
      }
    }
  }

  scene.textures.addCanvas(key, canvas);
  return { key, pxPerTile, texSize };
}

function hexToCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`;
}
